import { useState, useCallback } from 'react';
import { X } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@mustauto/ui/web';
import type { InventoryItem } from '@/hooks/useInventory';
import { formatReais } from '@/lib/utils';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useCan } from '@/hooks/useCan';
import { STOCK_UNITS, STOCK_UNIT_LABELS, normalizeStockUnit } from '@mustauto/utils';

interface ItemFormModalProps {
  item?: InventoryItem | null;
  onSubmit: (data: {
    name: string;
    sku?: string | null;
    category: string;
    unit?: string | null;
    partNumber?: string | null;
    location?: string | null;
    compatibleMakes?: string[];
    quantity?: number;
    minQuantity?: number;
    unitCost?: number;
    salePrice: number;
    supplier?: string | null;
  }) => Promise<void>;
  onClose: () => void;
  isSubmitting: boolean;
}

export function ItemFormModal({ item, onSubmit, onClose, isSubmitting }: ItemFormModalProps) {
  useEscapeKey(onClose);
  const trapRef = useFocusTrap<HTMLDivElement>();
  const isEditing = !!item;
  const [name, setName] = useState(item?.name ?? '');
  const [sku, setSku] = useState(item?.sku ?? '');
  const [category, setCategory] = useState(item?.category ?? '');
  const [unit, setUnit] = useState<string>(normalizeStockUnit(item?.unit) ?? 'unidade');
  const [partNumber, setPartNumber] = useState(item?.partNumber ?? '');
  const [location, setLocation] = useState(item?.location ?? '');
  const [makes, setMakes] = useState(item?.compatibleMakes?.join(', ') ?? '');
  const [quantity, setQuantity] = useState(String(item?.quantity ?? 0));
  const [minQuantity, setMinQuantity] = useState(String(item?.minQuantity ?? 1));
  const [unitCost, setUnitCost] = useState(String(item?.unitCost ?? ''));
  const [salePrice, setSalePrice] = useState(String(item?.salePrice ?? ''));
  const [supplier, setSupplier] = useState(item?.supplier ?? '');

  // Sem `estoque.custo` o formulário não tem o campo: o item nasce sem custo
  // (a primeira compra traz o real) e editar não mexe no custo gravado.
  const canSeeCost = useCan()('estoque.custo');
  const costNum = Number(unitCost) || 0;
  const priceNum = Number(salePrice) || 0;
  const margin = priceNum > 0 && costNum > 0 ? ((priceNum - costNum) / priceNum) * 100 : 0;

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const compatibleMakes = makes
        .split(',')
        .map((m) => m.trim())
        .filter(Boolean);
      await onSubmit({
        name,
        sku: sku || null,
        category,
        unit: unit || null,
        partNumber: partNumber || null,
        location: location || null,
        compatibleMakes: compatibleMakes.length > 0 ? compatibleMakes : undefined,
        ...(!isEditing && { quantity: Number(quantity) || 0 }),
        // `|| 1` transformava o mínimo 0 em 1: quem não quer alerta não conseguia.
        minQuantity: minQuantity.trim() === '' ? 1 : Number(minQuantity),
        ...(canSeeCost ? { unitCost: costNum } : {}),
        salePrice: priceNum,
        supplier: supplier || null,
      });
    },
    [name, sku, category, unit, partNumber, location, makes, quantity, minQuantity, unitCost, salePrice, supplier, isEditing, costNum, priceNum, canSeeCost, onSubmit],
  );

  return (
    <div ref={trapRef} role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Card className="w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base font-semibold">
            {isEditing ? 'Editar Item' : 'Novo Item de Estoque'}
          </CardTitle>
          <button onClick={onClose} aria-label="Fechar" className="rounded-md p-1 hover:bg-muted transition-colors">
            <X size={18} />
          </button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">Nome *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  maxLength={200}
                  placeholder="Ex: Pastilha de freio dianteira"
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">SKU</label>
                <input
                  type="text"
                  value={sku}
                  onChange={(e) => setSku(e.target.value)}
                  maxLength={100}
                  placeholder="Ex: PF-001"
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">Categoria *</label>
                <input
                  type="text"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  required
                  maxLength={100}
                  placeholder="Ex: Freios"
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">Unidade</label>
                <select
                  value={unit}
                  onChange={(e) => setUnit(e.target.value)}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  {STOCK_UNITS.map((u) => (
                    <option key={u} value={u}>
                      {STOCK_UNIT_LABELS[u]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">Part Number</label>
                <input
                  type="text"
                  value={partNumber}
                  onChange={(e) => setPartNumber(e.target.value)}
                  maxLength={100}
                  placeholder="Ex: 04465-33471"
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">Localização</label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  maxLength={100}
                  placeholder="Ex: Prateleira A3"
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
            </div>

            {isEditing && (
              // O campo some na edição de propósito (a quantidade muda por
              // movimentação, que fica no histórico) — mas sumir sem dizer
              // para onde foi é o que fez cliente perguntar "como altero a
              // quantidade depois de cadastrada".
              <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
                A quantidade em estoque não muda por aqui. Para somar, tirar ou corrigir a contagem, abra a
                peça e use <span className="font-medium text-foreground">Movimentar estoque</span>.
              </p>
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
              {!isEditing && (
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-muted-foreground">Qtd inicial</label>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    min={0}
                    // Fracionado (0,5 L, 30 ml): o passo padrão do navegador é 1.
                    step="0.001"
                    inputMode="decimal"
                    className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                  />
                </div>
              )}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">Qtd mínima</label>
                <input
                  type="number"
                  value={minQuantity}
                  onChange={(e) => setMinQuantity(e.target.value)}
                  min={0}
                  step="0.001"
                  inputMode="decimal"
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              {canSeeCost && (
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-muted-foreground">Custo unitário (R$) *</label>
                  <input
                    type="number"
                    value={unitCost}
                    onChange={(e) => setUnitCost(e.target.value)}
                    required
                    min={0}
                    step="0.01"
                    placeholder="0.00"
                    className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                  />
                </div>
              )}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">Preço de venda (R$) *</label>
                <input
                  type="number"
                  value={salePrice}
                  onChange={(e) => setSalePrice(e.target.value)}
                  required
                  min={0}
                  step="0.01"
                  placeholder="0.00"
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
            </div>

            {canSeeCost && costNum > 0 && priceNum > 0 && (
              <div className="rounded-md bg-muted/50 px-3 py-2 text-sm">
                Margem: <span className={`font-bold ${margin >= 30 ? 'text-emerald-600 dark:text-emerald-400' : margin >= 15 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`}>
                  {margin.toFixed(1)}%
                </span>
                {' '}({formatReais(priceNum - costNum)} por unidade)
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">Marcas compatíveis (separadas por vírgula)</label>
                <input
                  type="text"
                  value={makes}
                  onChange={(e) => setMakes(e.target.value)}
                  placeholder="Ex: Honda, Toyota, Hyundai"
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">Fornecedor</label>
                <input
                  type="text"
                  value={supplier}
                  onChange={(e) => setSupplier(e.target.value)}
                  maxLength={200}
                  placeholder="Ex: Auto Peças Silva"
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                type="submit"
                disabled={isSubmitting || !name.trim() || !category.trim() || (canSeeCost && !unitCost) || !salePrice}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {isSubmitting ? 'Salvando...' : isEditing ? 'Salvar alterações' : 'Adicionar item'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancelar
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
